import { TooltipDirective } from '../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../componentes/botones';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { IFilter, IQueryParam, IRecordatorio, IServicio } from 'modelos/src';
import { ContentCardComponent } from '../../../componentes/content-card/content-card.component';
import { LoadingComponent } from '../../../componentes/loading/loading.component';
import { DatePickerComponent } from '../../../componentes/date-picker/date-picker.component';
import { RecordatoriosService } from '../../../auxiliares/servicios/http/recordatorios.service';
import { ServiciosService } from '../../../auxiliares/servicios/http/servicios.service';
import { HelperService } from '../../../auxiliares/servicios/helper.service';

/** Ítem de la línea de tiempo, ya resuelto para pintar. */
interface ItemHistorial {
  icono: string;
  clase: 'recordatorio' | 'vencido' | 'presente' | 'servicio';
  titulo: string;
  valor: string | null;
  detalle: string | null;
  /** Referencias para el salto por km / por fecha */
  km: number | null;
  fechaMillis: number | null;
}

/** Techo de servicios a traer: evita bajar años de cargas de un vehículo viejo. */
const LIMITE_SERVICIOS = 300;

/**
 * Línea de tiempo del vehículo (misma info que IRIX Mobile, solo lectura):
 * recordatorios pendientes → odómetro actual → servicios pasados.
 *
 * El odómetro entra por input: el padre resuelve la fuente (reporte del equipo
 * o, si no reporta, el acumulado de Tripero).
 */
@Component({
  selector: 'app-historial-vehiculo',
  standalone: true,
  imports: [TooltipDirective, 
    CommonModule,
    FormsModule,
    BOTONES,
    
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    
    ContentCardComponent,
    LoadingComponent,
    DatePickerComponent,
  ],
  templateUrl: './historial-vehiculo.component.html',
  styleUrls: ['./historial-vehiculo.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HistorialVehiculoComponent implements OnChanges {
  private recordatoriosService = inject(RecordatoriosService);
  private serviciosService = inject(ServiciosService);

  /** Activo (vehículo o colectivo) del que se arma el historial. */
  @Input() idActivo?: string;

  /** Odómetro actual en km. Null si no hay dato de ninguna fuente. */
  @Input() odometroKm: number | null = null;

  /** True cuando el odómetro no sale del reporte del equipo sino calculado. */
  @Input() odometroCalculado = false;

  /**
   * Avisa si hay algo que mostrar (recordatorios o servicios). Sin eso la
   * línea de tiempo sería solo el nodo del odómetro, que ya está en otra
   * tarjeta: el padre esconde la columna entera.
   */
  @Output() tieneDatos = new EventEmitter<boolean>();

  public loading = signal(true);
  public items = signal<ItemHistorial[]>([]);
  public sinServicios = signal(false);
  public hayDatos = signal(false);

  /** Contenedor con el scroll de la línea de tiempo */
  @ViewChild('scroll') private scrollRef?: ElementRef<HTMLElement>;

  public mostrarBuscador = signal(false);
  public modoBusqueda = signal<'km' | 'fecha'>('km');
  public kmBuscado = signal<number | null>(null);
  public fechaBuscada = signal<string | null>(null);

  public toggleBuscador(): void {
    this.mostrarBuscador.update((v) => !v);
  }

  public irArriba(): void {
    this.scrollRef?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }

  public irAbajo(): void {
    const el = this.scrollRef?.nativeElement;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }

  public buscarPorKm(): void {
    const km = this.kmBuscado();
    if (km == null || isNaN(km)) return;
    this.saltarA(this.indiceMasProximo((i) => i.km, km));
  }

  public buscarPorFecha(iso: string | null): void {
    this.fechaBuscada.set(iso);
    if (!iso) return;
    const millis = new Date(iso).getTime();
    if (isNaN(millis)) return;
    this.saltarA(this.indiceMasProximo((i) => i.fechaMillis, millis));
  }

  /** Índice del ítem cuyo valor (km o fecha) está más cerca del buscado. */
  private indiceMasProximo(
    valor: (item: ItemHistorial) => number | null,
    objetivo: number,
  ): number | null {
    let mejor: number | null = null;
    let mejorDistancia = Infinity;
    this.items().forEach((item, i) => {
      const v = valor(item);
      if (v == null) return;
      const d = Math.abs(v - objetivo);
      if (d < mejorDistancia) {
        mejorDistancia = d;
        mejor = i;
      }
    });
    return mejor;
  }

  /**
   * Scrollea el contenedor hasta el ítem. Se calcula el delta contra el rect
   * del contenedor en vez de usar scrollIntoView, que además movería la página.
   */
  private saltarA(indice: number | null): void {
    const cont = this.scrollRef?.nativeElement;
    if (indice == null || !cont) return;
    const el = cont.querySelectorAll<HTMLElement>('.historial-item')[indice];
    if (!el) return;
    const delta =
      el.getBoundingClientRect().top - cont.getBoundingClientRect().top;
    cont.scrollTo({ top: cont.scrollTop + delta, behavior: 'smooth' });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['idActivo'] && this.idActivo) {
      this.cargar();
      return;
    }
    // El odómetro llega asincrónico (reporte / Tripero): recalcular sin re-pedir
    if (changes['odometroKm'] && !this.loading()) {
      this.armar(this.recordatorios, this.servicios);
    }
  }

  private recordatorios: IRecordatorio[] = [];
  private servicios: IServicio[] = [];

  private async cargar(): Promise<void> {
    if (!this.idActivo) return;
    this.loading.set(true);
    try {
      // notificado con $ne:true (no `false`): Mongo no matchea el campo ausente
      // contra false y los recordatorios viejos pueden no tenerlo.
      const filterRec: IFilter<IRecordatorio> = {
        idActivo: this.idActivo,
        notificado: { $ne: true },
      } as IFilter<IRecordatorio>;
      const queryRec: IQueryParam = {
        filter: JSON.stringify(filterRec),
        sort: 'kmLimite',
        limit: 50,
        // Un vehículo de un cliente hijo (el usuario trabaja como el padre):
        // sin esto la API filtra por el cliente propio y no trae nada.
        includeChildren: true,
      };
      const filterServ: IFilter<IServicio> = { idActivo: this.idActivo };
      // Orden por fechaCreacion (siempre presente) para que el recorte del
      // límite sea determinista; el orden visual se decide en ordenarServicios.
      const queryServ: IQueryParam = {
        filter: JSON.stringify(filterServ),
        sort: '-fechaCreacion',
        limit: LIMITE_SERVICIOS,
        includeChildren: true,
      };

      const [recordatorios, servicios] = await Promise.all([
        this.recordatoriosService.getFiltered(queryRec),
        this.serviciosService.getFiltered(queryServ),
      ]);

      this.recordatorios = recordatorios?.datos ?? [];
      this.servicios = servicios?.datos ?? [];
      this.armar(this.recordatorios, this.servicios);
      this.publicarSiHayDatos(
        this.recordatorios.length > 0 || this.servicios.length > 0,
      );
    } catch (error) {
      console.error('Error cargando el historial del vehículo', error);
      this.items.set([]);
      this.publicarSiHayDatos(false);
    } finally {
      this.loading.set(false);
    }
  }

  private publicarSiHayDatos(hay: boolean): void {
    if (this.hayDatos() === hay) return;
    this.hayDatos.set(hay);
    this.tieneDatos.emit(hay);
  }

  private armar(recordatorios: IRecordatorio[], servicios: IServicio[]): void {
    const odometro =
      this.odometroKm && this.odometroKm > 0 ? this.odometroKm : null;

    // Pendientes arriba (el más lejano primero) y vencidos abajo, pegados al
    // nodo del presente. km y días no son comparables: dentro de cada bloque
    // van primero los de fecha (típicamente anuales) y después los de km.
    const recs = recordatorios
      .map((r) => ({
        rec: r,
        restante: this.restanteRecordatorio(r, odometro),
      }))
      .sort((a, b) => {
        const vencidoA = a.restante.vencido ? 1 : 0;
        const vencidoB = b.restante.vencido ? 1 : 0;
        if (vencidoA !== vencidoB) return vencidoA - vencidoB;
        const fechaA = a.rec.fechaLimite ? 0 : 1;
        const fechaB = b.rec.fechaLimite ? 0 : 1;
        if (fechaA !== fechaB) return fechaA - fechaB;
        const ordenA = a.restante.vencido
          ? a.restante.orden
          : -a.restante.orden;
        const ordenB = b.restante.vencido
          ? b.restante.orden
          : -b.restante.orden;
        return ordenA - ordenB;
      });

    const items: ItemHistorial[] = recs.map(({ rec, restante }) => ({
      icono: restante.vencido ? 'warning' : 'flag',
      clase: restante.vencido ? 'vencido' : 'recordatorio',
      titulo: this.tituloRecordatorio(rec),
      valor: restante.valor,
      detalle: restante.detalle,
      km: rec.kmLimite ?? null,
      fechaMillis: rec.fechaLimite ? new Date(rec.fechaLimite).getTime() : null,
    }));

    // Nodo del presente
    items.push({
      icono: 'speed',
      clase: 'presente',
      titulo: 'Odómetro actual',
      valor: odometro != null ? this.formatKm(odometro) : 'sin dato',
      // Tripero es detalle de implementación: para el usuario, "calculado"
      detalle: odometro != null && this.odometroCalculado ? 'calculado' : null,
      km: odometro,
      fechaMillis: Date.now(),
    });

    const ordenados = this.ordenarServicios(servicios);
    this.sinServicios.set(ordenados.length === 0);

    for (const serv of ordenados) {
      items.push({
        icono: this.iconoServicio(serv.tipo),
        clase: 'servicio',
        titulo: this.tituloServicio(serv.tipo),
        // Solo la fecha de realización: la de carga se usa para ordenar,
        // no para mostrar.
        valor: serv.fechaRealizacion
          ? this.formatFecha(serv.fechaRealizacion)
          : null,
        detalle: this.detalleServicio(serv),
        km: serv.kmDelMantenimiento ?? null,
        // Solo indexa por fecha lo que muestra fecha, así el salto no manda a
        // una fila que aparece sin fecha
        fechaMillis: serv.fechaRealizacion
          ? new Date(serv.fechaRealizacion).getTime()
          : null,
      });
    }

    this.items.set(items);
  }

  /** Orden hacia el pasado: km desc, después fecha de realización, después de carga. */
  private ordenarServicios(servicios: IServicio[]): IServicio[] {
    return [...servicios].sort((a, b) => {
      const kmA = a.kmDelMantenimiento ?? null;
      const kmB = b.kmDelMantenimiento ?? null;
      if ((kmA != null ? 0 : 1) !== (kmB != null ? 0 : 1)) {
        return kmA != null ? -1 : 1;
      }
      if (kmA != null && kmB != null && kmA !== kmB) return kmB - kmA;
      const realA = a.fechaRealizacion ?? '';
      const realB = b.fechaRealizacion ?? '';
      if (realA !== realB) return realA < realB ? 1 : -1;
      const creA = a.fechaCreacion ?? '';
      const creB = b.fechaCreacion ?? '';
      if (creA !== creB) return creA < creB ? 1 : -1;
      return 0;
    });
  }

  /**
   * Cuánto falta para un recordatorio. `orden` es la magnitud restante en su
   * propia unidad (km o días): solo se compara con recordatorios de igual unidad.
   * Con km y fecha a la vez manda el que ya venció; si no, la fecha.
   */
  private restanteRecordatorio(
    rec: IRecordatorio,
    odometro: number | null,
  ): {
    vencido: boolean;
    orden: number;
    valor: string | null;
    detalle: string | null;
  } {
    const kmLimite = rec.kmLimite && rec.kmLimite > 0 ? rec.kmLimite : null;
    const frecuenciaKm = (rec as { frecuenciaKm?: number }).frecuenciaKm;
    const frecuenciaDia = (rec as { frecuenciaDia?: number }).frecuenciaDia;

    let porKm: {
      vencido: boolean;
      orden: number;
      valor: string | null;
      detalle: string | null;
    } | null = null;
    if (kmLimite != null) {
      const faltan = odometro != null ? kmLimite - odometro : null;
      const vencido = faltan != null && faltan <= 0;
      const detalle: string[] = [`a los ${this.formatKm(kmLimite)}`];
      if (vencido && faltan != null) {
        detalle.push(`superado por ${this.formatKm(-faltan)}`);
      }
      if (frecuenciaKm) detalle.push(`cada ${this.formatKm(frecuenciaKm)}`);
      porKm = {
        vencido,
        orden: faltan ?? kmLimite,
        valor:
          faltan == null
            ? null
            : vencido
              ? 'vencido'
              : `en ${this.formatKm(faltan)}`,
        detalle: detalle.join(' · '),
      };
    }

    let porFecha: {
      vencido: boolean;
      orden: number;
      valor: string | null;
      detalle: string | null;
    } | null = null;
    if (rec.fechaLimite) {
      const dias = this.diasHasta(rec.fechaLimite);
      const vencido = dias != null && dias < 0;
      const detalle: string[] = [
        `vence el ${this.formatFechaCorta(rec.fechaLimite)}`,
      ];
      if (vencido && dias != null) detalle.push(`hace ${-dias} días`);
      if (frecuenciaDia) detalle.push(`cada ${frecuenciaDia} días`);
      let valor: string | null = null;
      if (dias != null) {
        if (dias < 0) valor = 'vencido';
        else if (dias === 0) valor = 'hoy';
        else if (dias === 1) valor = 'mañana';
        else valor = `en ${dias} días`;
      }
      porFecha = {
        vencido,
        orden: dias ?? 0,
        valor,
        detalle: detalle.join(' · '),
      };
    }

    if (porKm && porFecha) {
      return porKm.vencido && !porFecha.vencido ? porKm : porFecha;
    }
    return (
      porFecha ??
      porKm ?? { vencido: false, orden: 0, valor: null, detalle: null }
    );
  }

  private tituloRecordatorio(rec: IRecordatorio): string {
    const subcategoria = (rec as { subcategoria?: string }).subcategoria;
    if (subcategoria) return subcategoria;
    if (rec.detallesDelMantenimiento) return rec.detallesDelMantenimiento;
    return rec.idDocumentacion
      ? 'Vencimiento de documentación'
      : 'Mantenimiento';
  }

  private iconoServicio(tipo?: string): string {
    if (tipo === 'Combustible') return 'local_gas_station';
    if (tipo === 'Mantenimiento') return 'build';
    return 'payments';
  }

  private tituloServicio(tipo?: string): string {
    if (tipo === 'Combustible') return 'Carga de combustible';
    if (tipo === 'Mantenimiento') return 'Mantenimiento';
    if (tipo === 'Gasto') return 'Gasto';
    return tipo ?? 'Servicio';
  }

  private detalleServicio(serv: IServicio): string | null {
    const partes: string[] = [];
    if (serv.litrosCargados != null) partes.push(`${serv.litrosCargados} L`);
    if (serv.costo != null) partes.push(this.formatCosto(serv.costo));
    if (serv.kmDelMantenimiento != null) {
      partes.push(this.formatKm(serv.kmDelMantenimiento));
    }
    if (serv.detalles) partes.push(serv.detalles);
    return partes.length ? partes.join(' · ') : null;
  }

  private formatKm(km: number): string {
    return `${Math.round(km).toLocaleString('es-AR')} km`;
  }

  private formatCosto(monto: number): string {
    return `$${Math.round(monto).toLocaleString('es-AR')}`;
  }

  private formatFecha(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return HelperService.parseDate(d);
  }

  private formatFechaCorta(iso: string): string {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : HelperService.parseDate(d).slice(0, 10);
  }

  /** Días calendario hasta una fecha (negativo si ya pasó). */
  private diasHasta(iso: string): number | null {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const unDia = 24 * 60 * 60 * 1000;
    return Math.floor((d.getTime() - Date.now()) / unDia);
  }
}
